import user from 'js/lib/user';

import { useNaptime } from 'bundles/naptimejs';
import ProfileConsentsV1 from 'bundles/naptimejs/resources/profileConsents.v1';
import { PROFILE_CONSENT_COOKIE_CONSENT_NAMES } from 'bundles/user-consent/constants';
import useEnterpriseUserInfo from 'bundles/user-consent/hooks/useEnterpriseUserInfo';
import type { ProfileConsent } from 'bundles/user-consent/types';
import { isMissingCookieConsent } from 'bundles/user-consent/utils';

type ProfileConsentData = {
  profileConsents?: ProfileConsent;
};

const useProfileConsents = (userId: number, shouldSkipQuery = false) => {
  const {
    data: { profileConsents },
    pending: isProfileConsentsLoading,
  } = useNaptime<ProfileConsentData>(() => {
    const isMissingCCPAConsentCookie = isMissingCookieConsent(PROFILE_CONSENT_COOKIE_CONSENT_NAMES.ccpaRequired);
    const isMissingGDPRConsentCookie = isMissingCookieConsent(PROFILE_CONSENT_COOKIE_CONSENT_NAMES.gdprRequired);

    if (shouldSkipQuery || (!isMissingCCPAConsentCookie && !isMissingGDPRConsentCookie)) {
      return {};
    }

    return {
      profileConsents: ProfileConsentsV1.get(userId, {
        fields: [
          ...(isMissingGDPRConsentCookie ? ['requireGdprConsent'] : []),
          ...(isMissingCCPAConsentCookie ? ['requireCcpaConsent'] : []),
        ],
      }),
    };
  }, [userId]);

  return { profileConsents, isProfileConsentsLoading };
};

const useMarketingConsentData = (userAccountId?: number, shouldSkipQuery = false) => {
  const userId = userAccountId || user.get().id;
  const { profileConsents, isProfileConsentsLoading } = useProfileConsents(userId, shouldSkipQuery);
  const requireCCPAConsent = !!profileConsents?.requireCcpaConsent;
  const requireGDPRConsent = !!profileConsents?.requireGdprConsent;
  const shouldSkipEnterpriseQuery = shouldSkipQuery || (!requireCCPAConsent && !requireGDPRConsent);
  const {
    isEnterpriseAdmin,
    isRestrictedEnterpriseUser,
    isLoading: isEnterpriseUserInfoLoading,
  } = useEnterpriseUserInfo(shouldSkipEnterpriseQuery, userId);
  const shouldShowCCPAMarketingConsent = !!(requireCCPAConsent && !isRestrictedEnterpriseUser);
  const shouldShowGDPRMarketingConsent = !!(requireGDPRConsent && !isRestrictedEnterpriseUser);
  const isLoading = isProfileConsentsLoading || isEnterpriseUserInfoLoading;

  return { shouldShowCCPAMarketingConsent, shouldShowGDPRMarketingConsent, isEnterpriseAdmin, isLoading };
};

export default useMarketingConsentData;
